/**
 * Work Order Form Component
 * Task 60: Form for creating/editing work orders with diagnosis, labor, and parts costs
 */

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { IndustryWorkOrder } from '@/types/industry';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';

const workOrderSchema = z.object({
  vehicle_id: z.coerce.number().min(1, 'Veículo é obrigatório'),
  start_date: z.date(),
  status: z.enum(['pending', 'in_progress', 'completed', 'cancelled']),
  diagnosis: z.string().min(1, 'Diagnóstico é obrigatório'),
  work_performed: z.string().optional(),
  labor_cost: z.coerce.number().min(0),
  parts_cost: z.coerce.number().min(0),
  notes: z.string().optional(),
});

type WorkOrderFormData = z.infer<typeof workOrderSchema>;

interface WorkOrderFormProps {
  workOrder?: IndustryWorkOrder;
  vehicles: Array<{ id: number; plate: string; make: string; model: string }>;
  onSubmit: (data: WorkOrderFormData) => void | Promise<void>;
  onCancel?: () => void;
  isSubmitting?: boolean;
}

export function WorkOrderForm({ workOrder, vehicles, onSubmit, onCancel, isSubmitting = false }: WorkOrderFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm<WorkOrderFormData>({
    resolver: zodResolver(workOrderSchema),
    defaultValues: workOrder
      ? {
          vehicle_id: workOrder.vehicle_id!,
          start_date: workOrder.start_date ? new Date(workOrder.start_date) : new Date(),
          status: workOrder.status as any,
          diagnosis: (workOrder.metadata?.diagnosis as string) || '',
          work_performed: (workOrder.metadata?.workPerformed as string) || '',
          labor_cost: (workOrder.metadata?.laborCost as number) || 0,
          parts_cost: (workOrder.metadata?.partsCost as number) || 0,
          notes: (workOrder.metadata?.notes as string) || '',
        }
      : {
          start_date: new Date(),
          status: 'pending',
          labor_cost: 0,
          parts_cost: 0,
        },
  });

  const startDate = watch('start_date');
  const selectedStatus = watch('status');
  const selectedVehicle = watch('vehicle_id');
  const laborCost = watch('labor_cost') || 0;
  const partsCost = watch('parts_cost') || 0;
  const totalCost = laborCost + partsCost;

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className="space-y-6">
        {/* Vehicle and Date */}
        <Card>
          <CardHeader>
            <CardTitle>Informações Básicas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Veículo *</Label>
                <Select
                  value={selectedVehicle?.toString()}
                  onValueChange={(v) => setValue('vehicle_id', parseInt(v))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o veículo" />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id.toString()}>
                        <span className="font-mono">{v.plate}</span> - {v.make} {v.model}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.vehicle_id && (
                  <p className="text-sm text-destructive">{errors.vehicle_id.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Data de Entrada *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className={cn('w-full justify-start', !startDate && 'text-muted-foreground')}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {startDate ? format(startDate, 'dd/MM/yyyy') : 'Selecione'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(d) => d && setValue('start_date', d)}
                      locale={ptBR}
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Status *</Label>
              <Select value={selectedStatus} onValueChange={(v: any) => setValue('status', v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Aguardando</SelectItem>
                  <SelectItem value="in_progress">Em Andamento</SelectItem>
                  <SelectItem value="completed">Concluído</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Diagnosis and Work */}
        <Card>
          <CardHeader>
            <CardTitle>Diagnóstico e Serviço</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Diagnóstico *</Label>
              <Textarea {...register('diagnosis')} rows={3} placeholder="Problema identificado" />
              {errors.diagnosis && (
                <p className="text-sm text-destructive">{errors.diagnosis.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Serviço Realizado</Label>
              <Textarea
                {...register('work_performed')}
                rows={3}
                placeholder="Descrição do trabalho executado"
              />
              <p className="text-xs text-muted-foreground">
                Detalhes dos serviços e peças substituídas
              </p>
            </div>

            <div className="space-y-2">
              <Label>Observações</Label>
              <Textarea {...register('notes')} rows={2} placeholder="Informações adicionais" />
            </div>
          </CardContent>
        </Card>

        {/* Costs */}
        <Card>
          <CardHeader>
            <CardTitle>Valores</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Mão de Obra (R$) *</Label>
                <Input type="number" step="0.01" {...register('labor_cost')} />
                {errors.labor_cost && (
                  <p className="text-sm text-destructive">{errors.labor_cost.message}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label>Peças (R$) *</Label>
                <Input type="number" step="0.01" {...register('parts_cost')} />
                {errors.parts_cost && (
                  <p className="text-sm text-destructive">{errors.parts_cost.message}</p>
                )}
              </div>
            </div>

            <div className="rounded-md bg-muted p-4">
              <p className="text-sm font-medium">
                Valor Total:{' '}
                {new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                }).format(totalCost)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-2 justify-end">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
              Cancelar
            </Button>
          )}
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : workOrder ? 'Atualizar' : 'Criar'} OS
          </Button>
        </div>
      </div>
    </form>
  );
}
